import { PROVIDER_TYPES } from '@/orchestrate/journeys/integrations.constants';
import { EMAIL_CONTENT_SOURCE } from '@/emails/constants';
import { i18n_t as $t } from '@/utils/i18n';

const { HUBSPOT, MARKETO, ELOQUA } = PROVIDER_TYPES;

export const NO_MESSAGES_INTERACTION = {
    id: 'noMessagesInteraction',
    label: 'No message engagement',
    prefix: 'noMessagesInteraction',
};

export const MESSAGE_TYPE_ICONS = Object.freeze({
    AWS: {
        key: 'EMAIL',
        label: 'Email',
        capitalLabel: 'Email',
        icon: {
            symbol: 'mail',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-magenta-80)',
            size: 10,
        },
    },
    GUIDE: {
        key: 'GUIDE',
        label: 'Guide',
        capitalLabel: 'Guide',
        icon: {
            symbol: 'guide',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-green-70)',
            size: 10,
        },
    },
    HUBSPOT: {
        key: 'HUBSPOT',
        label: 'HubSpot',
        capitalLabel: 'HubSpot email',
        icon: {
            symbol: 'hubspot',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-orange-70)',
            fillColor: 'var(--palette-gray-0)',
            strokeWidth: 0,
            size: 16,
        },
    },
    MARKETO: {
        key: 'MARKETO',
        label: 'Marketo',
        capitalLabel: 'Marketo campaign',
        icon: {
            symbol: 'marketo',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-purple-80)',
            fillColor: 'var(--palette-gray-0)',
            strokeWidth: 0,
            size: 10,
        },
    },
    ELOQUA: {
        key: 'ELOQUA',
        label: 'Eloqua',
        capitalLabel: 'Oracle Eloqua email',
        icon: {
            symbol: 'eloqua',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--third-party-oracle-red)',
            fillColor: 'var(--palette-gray-0)',
            strokeWidth: 0,
            size: 16,
        },
    },
    CONDITION: {
        key: 'CONDITION',
        label: 'Conditional split',
        capitalLabel: 'Conditional split',
        icon: {
            symbol: 'or',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-blue-70)',
            strokeWidth: 0,
            size: 10,
            style: { transform: 'rotate(90deg)' },
        },
    },
    START: {
        key: 'START',
        label: 'Start',
        capitalLabel: 'Start',
        icon: {
            symbol: 'log-in',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-blue-80)',
            size: 10,
        },
    },
    EXIT: {
        key: 'EXIT',
        label: 'Exit',
        capitalLabel: 'Exit',
        icon: {
            symbol: 'log-out',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-gray-110)',
            size: 10,
        },
    },
});

export const MESSAGE_DROPDOWN_CATEGORIES = Object.freeze({
    GUIDES: {
        key: 'GUIDES',
        capitalLabel: 'Guides',
        icon: {
            symbol: 'guide',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-green-70)',
            size: 10,
        },
    },
    EMAIL: {
        key: 'EMAIL',
        capitalLabel: 'Email',
        icon: {
            symbol: 'mail',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-magenta-80)',
            size: 10,
        },
    },
    INTEGRATIONS: {
        key: 'INTEGRATIONS',
        capitalLabel: 'Integrations',
        icon: {
            symbol: 'integration',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-purple-70)',
            size: 10,
        },
    },
    ACTIONS: {
        key: 'ACTIONS',
        capitalLabel: 'Actions',
        icon: {
            symbol: 'or',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-blue-70)',
            size: 10,
            style: { transform: 'rotate(90deg)' },
        },
    },
});

export const ADD_MESSAGE_OPTIONS = {
    ADD_HUBSPOT_EMAIL: $t('journeysConstants.Add HubSpot email'),
    ADD_MARKETO_EMAIL: $t('journeysConstants.Add Marketo campaign'),
    ADD_ELOQUA_EMAIL: $t('journeysConstants.Add Oracle Eloqua email'),
    CREATE_GUIDE: $t('journeysConstants.Create new guide'),
    CLONE_EMAIL: $t('journeysConstants.Clone existing email'),
    CLONE_GUIDE: $t('journeysConstants.Clone existing guide'),
    CONDITIONAL_SPLIT: $t('journeysConstants.Conditional split'),
    CONNECT_TO_STEP: $t('journeysConstants.Connect to step'),
    CREATE_EMAIL: $t('journeysConstants.Create new email'),
    CREATE_TEMPLATE_EMAIL: $t('emails.Create_template_email'),
    CREATE_HTML_EMAIL: $t('emails.Create_HTML_email'),
};

export const MESSAGE_TYPES = {
    GUIDE: 'Guide',
    EMAIL: 'Email',
};

export const MESSAGE_SIDE_BLADE_CONFIG = Object.freeze({
    [MESSAGE_TYPES.GUIDE]: {
        contentVariant: MESSAGE_TYPES.GUIDE,
        showSettingsTab: false,
        showThrottlingSection: false,
        usesGuideLocalization: true,
        usesEmailLocalization: false,
        durationUsesGuideLabel: true,
        requiresGuideForPreview: true,
    },
    [MESSAGE_TYPES.EMAIL]: {
        showSettingsTab: true,
        showThrottlingSection: true,
        usesGuideLocalization: false,
        usesEmailLocalization: true,
        durationUsesGuideLabel: false,
        byContentSource: {
            [EMAIL_CONTENT_SOURCE.BLOCKS]: {
                contentVariant: EMAIL_CONTENT_SOURCE.BLOCKS,
                requiresGuideForPreview: true,
            },
            [EMAIL_CONTENT_SOURCE.HTML]: {
                contentVariant: EMAIL_CONTENT_SOURCE.HTML,
                requiresGuideForPreview: false,
            },
        },
    },
});

// eslint-disable-next-line no-warning-comments
// TODO: ENGAGEMENT_GOAL_TYPES should be GOAL_TYPES
export const ENGAGEMENT_GOAL_TYPES = {
    APPLICATION: 'application',
    NO_GOAL: 'noGoal',
};

export const GOAL_DETAILS_BY_KIND = {
    Feature: {
        actionDescription: 'Visitors clicked',
        icon: 'feature',
    },
    Page: {
        actionDescription: 'Visitors viewed',
        icon: 'page',
    },
    TrackType: {
        actionDescription: 'Initiate',
        icon: 'zap',
    },
    [ENGAGEMENT_GOAL_TYPES.NO_GOAL]: {
        actionDescription: 'No goal',
    },
};

export const JOURNEY_CATEGORIES = Object.freeze({
    START_FROM_SCRATCH: {
        name: '',
        icon: {
            symbol: 'plus',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-green-60)',
            size: 18,
            padding: '4px',
        },
    },
    ONBOARDING: {
        name: 'Onboarding',
        icon: {
            symbol: 'handshake',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-blue-60)',
            size: 18,
            padding: '4px',
        },
    },
    NEW_RELEASE: {
        name: 'New release',
        icon: {
            symbol: 'announcement',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-orange-70)',
            size: 18,
            padding: '4px',
        },
    },
    REENGAGEMENT: {
        name: 'Reengagement',
        icon: {
            symbol: 'reengagement',
            strokeColor: 'var(--palette-gray-0)',
            backgroundColor: 'var(--palette-magenta-80)',
            size: 18,
            padding: '4px',
        },
    },
});

export const ERROR_MESSAGES = {
    JOURNEY_CREATION: {
        title: 'Error creating journey',
        message: "We're unable to create a journey. Try again or contact support if the issue continues.",
    },
    MISSING_MESSAGE: {
        title: 'Missing template messages',
        message:
            'A message in the template is missing. You can continue with the other messages or delete the journey and start again.',
    },
    JOURNEY_UPDATE: {
        title: 'Error updating journey',
        message: "We're unable to update the journey. Please refresh your browser and try again.",
    },
    JOURNEY_NOT_FOUND: {
        title: 'Journey not found',
        message: "We're unable to find this journey. Please check the URL or contact support if the issue continues.",
        status: 404,
    },
    JOURNEY_STATUS_UPDATE: {
        title: 'Error updating journey status',
        message: "We're unable to update the journey status. Please refresh your browser and try again.",
    },
};

export const BRANCHING_NODES_TYPES = {
    START: 'Start',
    EXIT: 'Exit',
    MESSAGE: 'Message',
    CONDITION: 'Condition',
};

export const BRANCHING_EDGES_TYPES = {
    YES: 'Yes',
    NO: 'No',
    REGULAR: 'Regular',
};

export const MAX_MESSAGES = 20;

export const MAX_CONDITION_CHAIN = 4;

export const JOURNEY_LEARN_MORE_LINK =
    'https://support.pendo.io/hc/en-us/articles/28029722249115-Create-a-journey#h_01K04SH1AG27N8RMTKA34HXTVN';

export const MESSAGE_DROPDOWN_OPTIONS = [
    {
        showIf: 'hasCreateJourneyPermission',
        productLine: ['guides'],
        category: MESSAGE_DROPDOWN_CATEGORIES.GUIDES.key,
        items: [
            {
                label: ADD_MESSAGE_OPTIONS.CREATE_GUIDE,
                type: MESSAGE_TYPES.GUIDE,
                disabledIf: 'isMaxMessagesPassed',
                description: 'maxMessagesDescription',
            },
            {
                label: ADD_MESSAGE_OPTIONS.CLONE_GUIDE,
                type: MESSAGE_TYPES.GUIDE,
                disabledIf: 'isMaxMessagesPassed',
                description: 'maxMessagesDescription',
            },
        ],
    },
    {
        showIf: 'hasCreateJourneyPermission',
        productLine: ['orchestrate', 'emailInJourneyEnabled'],
        category: MESSAGE_DROPDOWN_CATEGORIES.EMAIL.key,
        items: [
            {
                label: ADD_MESSAGE_OPTIONS.CREATE_EMAIL,
                type: MESSAGE_TYPES.EMAIL,
                provider: PROVIDER_TYPES.AWS,
                disabledIf: 'isMaxMessagesPassed',
                description: 'maxMessagesDescription',
                items: [
                    {
                        label: ADD_MESSAGE_OPTIONS.CREATE_TEMPLATE_EMAIL,
                        type: MESSAGE_TYPES.EMAIL,
                        provider: PROVIDER_TYPES.AWS,
                        disabledIf: 'isMaxMessagesPassed',
                        description: 'maxMessagesDescription',
                        segmentFlag: 'htmlEmailsInJourney',
                    },
                    {
                        label: ADD_MESSAGE_OPTIONS.CREATE_HTML_EMAIL,
                        type: MESSAGE_TYPES.EMAIL,
                        provider: PROVIDER_TYPES.AWS,
                        disabledIf: 'isMaxMessagesPassed',
                        description: 'maxMessagesDescription',
                        segmentFlag: 'htmlEmailsInJourney',
                    },
                ],
            },
            {
                label: ADD_MESSAGE_OPTIONS.CLONE_EMAIL,
                type: MESSAGE_TYPES.EMAIL,
                provider: PROVIDER_TYPES.AWS,
                disabledIf: 'isMaxMessagesPassed',
                description: 'maxMessagesDescription',
            },
        ],
    },
    {
        showIf: 'hasIntegrationsEnabled',
        category: MESSAGE_DROPDOWN_CATEGORIES.INTEGRATIONS.key,
        items: [
            {
                label: ADD_MESSAGE_OPTIONS.ADD_HUBSPOT_EMAIL,
                type: MESSAGE_TYPES.EMAIL,
                provider: PROVIDER_TYPES.HUBSPOT,
                disabledIf: 'isMaxMessagesPassed',
                description: 'maxMessagesDescription',
                segmentFlag: 'hubspotEmail',
                productLine: ['orchestrate', 'hubspotEmailIntegrationEnabled'],
            },
            {
                label: ADD_MESSAGE_OPTIONS.ADD_MARKETO_EMAIL,
                type: MESSAGE_TYPES.EMAIL,
                provider: PROVIDER_TYPES.MARKETO,
                disabledIf: 'isMaxMessagesPassed',
                description: 'maxMessagesDescription',
                segmentFlag: 'marketoEmail',
                productLine: ['orchestrate', 'marketoEmailIntegrationEnabled'],
            },
            {
                label: ADD_MESSAGE_OPTIONS.ADD_ELOQUA_EMAIL,
                type: MESSAGE_TYPES.EMAIL,
                provider: PROVIDER_TYPES.ELOQUA,
                disabledIf: 'isMaxMessagesPassed',
                description: 'maxMessagesDescription',
                productLine: ['orchestrate', 'eloquaEmailIntegrationEnabled'],
            },
        ],
    },
    {
        showIf: 'journeyHasMessageGraph',
        category: MESSAGE_DROPDOWN_CATEGORIES.ACTIONS.key,
        items: [
            {
                label: ADD_MESSAGE_OPTIONS.CONDITIONAL_SPLIT,
            },
            {
                label: ADD_MESSAGE_OPTIONS.CONNECT_TO_STEP,
                /* disabled and description are handled individually in the edge-with-add-node component */
                items: [],
            },
        ],
    },
];

export const PROVIDER_FIELDS = {
    [HUBSPOT]: [
        { key: 'subject', label: 'Subject line' },
        { key: 'friendlyFromName', label: 'Sender name' },
        { key: 'from', label: 'From address' },
        { key: 'replyTo', label: 'Reply-to address' },
    ],
    [MARKETO]: [
        { key: 'marketoProgramName', label: 'Program name' },
        { key: 'description', label: 'Description' },
    ],
    [ELOQUA]: [
        { key: 'subject', label: 'Subject line' },
    ],
};

export const getProviderFields = (provider) => PROVIDER_FIELDS[provider] || [];
